import React, { useMemo, useState } from 'react';
import moment from 'moment-timezone';
import Spinner from '../../../components/common/spinner';
import ToggleButton from '../../../components/common/toggle-button';
import {
  JobControl,
  JobExecutorPreference,
  JobKind,
  JobLastRun,
  useJobControls,
  useRunBackgroundJob,
  useUpdateJobControl,
} from '../../../hooks/useJobControls';

type KindFilter = 'all' | JobKind;

/** Backend DateTime fields are naive UTC ISO (no Z). Treat missing offset as UTC. */
const parseUtcMoment = (iso: string) => {
  const normalized = /Z$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  return moment.utc(normalized);
};

/** Scheduler heartbeat stays in UTC with an explicit label (avoid bare "Z"). */
const formatUtc = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const m = parseUtcMoment(iso);
  if (!m.isValid()) return iso;
  return `${m.format('YYYY-MM-DD HH:mm:ss')} UTC`;
};

/**
 * Last/next run in the job's schedule timezone so wall-clock matches Schedule.
 * Falls back to UTC when timezone is missing/invalid.
 */
const formatInTimezone = (iso: string | null | undefined, timezone: string | null | undefined) => {
  if (!iso) return '—';
  const m = parseUtcMoment(iso);
  if (!m.isValid()) return iso;
  const tz = (timezone || 'UTC').trim() || 'UTC';
  const local = m.tz(tz);
  if (!local.isValid()) {
    return `${m.format('YYYY-MM-DD HH:mm:ss')} UTC`;
  }
  return local.format('YYYY-MM-DD HH:mm:ss z');
};

const lastRunLabel = (lastRun: JobLastRun | null | undefined) => {
  if (!lastRun) return null;
  const when = lastRun.completed_at || lastRun.started_at || lastRun.queued_at;
  return { status: lastRun.status, when, id: lastRun.id };
};

const JobControlsTab: React.FC = () => {
  const { data, isLoading, refetch } = useJobControls();
  const { mutate: updateControl, isLoading: isUpdating } = useUpdateJobControl();
  const { mutate: runJob, isLoading: isRunning } = useRunBackgroundJob();

  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [runTarget, setRunTarget] = useState<JobControl | null>(null);
  const [argsText, setArgsText] = useState('[]');
  const [kwargsText, setKwargsText] = useState('{}');
  const [runError, setRunError] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<JobControl | null>(null);
  const [scheduleDraft, setScheduleDraft] = useState('');
  const [timezoneDraft, setTimezoneDraft] = useState('America/Chicago');

  const rows = useMemo(() => {
    const list = data?.data || [];
    const q = search.trim().toLowerCase();
    return list.filter((row) => {
      if (kindFilter !== 'all' && row.job_kind !== kindFilter) return false;
      if (!q) return true;
      const haystack = `${row.handler_key} ${row.label || ''} ${row.description || ''} ${
        row.schedule_expr || ''
      }`.toLowerCase();
      return haystack.includes(q);
    });
  }, [data, search, kindFilter]);

  const scheduler = data?.scheduler;

  const openRunModal = (row: JobControl) => {
    setRunTarget(row);
    setArgsText(JSON.stringify(row.example_args ?? [], null, 2));
    setKwargsText(JSON.stringify(row.example_kwargs ?? {}, null, 2));
    setRunError(null);
  };

  const openScheduleModal = (row: JobControl) => {
    setEditingSchedule(row);
    setScheduleDraft(row.schedule_expr || '');
    setTimezoneDraft(row.timezone || 'America/Chicago');
  };

  const submitSchedule = () => {
    if (!editingSchedule) return;
    updateControl(
      {
        handlerKey: editingSchedule.handler_key,
        update: {
          schedule_expr: scheduleDraft.trim(),
          timezone: timezoneDraft.trim() || 'UTC',
        },
      },
      { onSuccess: () => setEditingSchedule(null) },
    );
  };

  const submitRun = () => {
    if (!runTarget) return;
    setRunError(null);

    if (runTarget.job_kind === 'scheduled') {
      runJob({ handler_key: runTarget.handler_key, force: true }, { onSuccess: () => setRunTarget(null) });
      return;
    }

    let args: unknown[];
    let kwargs: Record<string, unknown>;
    try {
      args = JSON.parse(argsText);
      if (!Array.isArray(args)) throw new Error('args must be a JSON array');
    } catch (e: any) {
      setRunError(`Invalid args JSON: ${e.message || e}`);
      return;
    }
    try {
      kwargs = JSON.parse(kwargsText);
      if (!kwargs || Array.isArray(kwargs) || typeof kwargs !== 'object') {
        throw new Error('kwargs must be a JSON object');
      }
    } catch (e: any) {
      setRunError(`Invalid kwargs JSON: ${e.message || e}`);
      return;
    }

    const lastDot = runTarget.handler_key.lastIndexOf('.');
    const module = runTarget.handler_key.slice(0, lastDot);
    const functionName = runTarget.handler_key.slice(lastDot + 1);

    runJob({ module, function: functionName, args, kwargs, force: true }, { onSuccess: () => setRunTarget(null) });
  };

  const setEnabled = (row: JobControl, enabled: boolean) => {
    updateControl({ handlerKey: row.handler_key, update: { enabled } });
  };

  const setExecutor = (row: JobControl, executor: JobExecutorPreference) => {
    updateControl({ handlerKey: row.handler_key, update: { executor } });
  };

  if (isLoading) {
    return (
      <div className="p-7">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="px-7 py-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Job Controls</h2>
          <p className="text-sm text-gray-600">
            Manage fire-and-forget handlers and scheduled daemons: enable/disable, runner preference, schedules
            (DB-backed), and Run now.
          </p>
        </div>
        <button
          className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          onClick={() => refetch()}
        >
          Refresh
        </button>
      </div>

      {scheduler?.stale && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Scheduler heartbeat is stale
          {scheduler.last_tick_at
            ? ` (last tick ${formatUtc(scheduler.last_tick_at)}, age ${Math.round(scheduler.age_seconds ?? 0)}s)`
            : ' (no tick recorded)'}
          . Backend daemon may be down — schedule edits will not fire until it recovers.
        </div>
      )}
      {scheduler && !scheduler.stale && (
        <div className="mb-4 text-xs text-gray-500">
          Scheduler last tick {formatUtc(scheduler.last_tick_at)}
          {scheduler.age_seconds != null ? ` (${Math.round(scheduler.age_seconds)}s ago)` : ''}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search handlers or schedules…"
          className="w-full max-w-md rounded border border-gray-300 px-3 py-2 text-sm"
        />
        <div className="flex rounded border border-gray-300 text-sm">
          {(
            [
              ['all', 'All'],
              ['faf', 'Fire-and-forget'],
              ['scheduled', 'Scheduled'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`px-3 py-1.5 ${kindFilter === value ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setKindFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Job</th>
              <th className="px-4 py-3">Kind</th>
              <th className="px-4 py-3">Schedule</th>
              <th className="px-4 py-3">Last / next</th>
              <th className="px-4 py-3">Enabled</th>
              <th className="px-4 py-3">Runner</th>
              <th className="px-4 py-3">Effective</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map((row) => {
              const last = lastRunLabel(row.last_run);
              return (
                <tr key={row.handler_key} className={!row.enabled ? 'bg-gray-50 opacity-80' : undefined}>
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-gray-900">{row.label || row.handler_key}</div>
                    <div className="font-mono text-xs text-gray-500">{row.handler_key}</div>
                    {row.description && <div className="mt-1 text-xs text-gray-600">{row.description}</div>}
                    {row.hard_denylisted && row.job_kind === 'faf' && (
                      <div className="mt-1 text-xs font-medium text-amber-700">
                        Hard denylisted in config (auto stays backend)
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-600">
                    {row.job_kind === 'scheduled' ? 'scheduled' : 'faf'}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {row.job_kind === 'scheduled' ? (
                      <div>
                        <div className="font-mono text-xs text-gray-800">{row.schedule_expr || '—'}</div>
                        <div className="text-xs text-gray-500">{row.timezone || 'UTC'}</div>
                        <button
                          type="button"
                          className="mt-1 text-xs font-medium text-blue-700 hover:underline"
                          onClick={() => openScheduleModal(row)}
                          disabled={isUpdating}
                        >
                          Edit
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-700">
                    {row.job_kind === 'scheduled' ? (
                      <div className="space-y-1">
                        <div>
                          <span className="text-gray-500">Last:</span>{' '}
                          {last ? (
                            <>
                              <span className="font-medium">{last.status}</span>
                              <span className="text-gray-500"> #{last.id}</span>
                              <div className="font-mono text-[11px] text-gray-500">
                                {formatInTimezone(last.when, row.timezone)}
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-400">no FAF yet</span>
                          )}
                        </div>
                        <div>
                          <span className="text-gray-500">Next:</span>{' '}
                          <span className="font-mono text-[11px]">{formatInTimezone(row.next_run, row.timezone)}</span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <ToggleButton
                      id={`enabled-${row.handler_key}`}
                      status={row.enabled}
                      setStatus={(checked: boolean) => setEnabled(row, checked)}
                      disabled={isUpdating}
                    />
                  </td>
                  <td className="px-4 py-3 align-top">
                    {row.executor_locked ? (
                      <span className="text-xs text-gray-600">backend (locked)</span>
                    ) : (
                      <select
                        className="rounded border border-gray-300 px-2 py-1 text-sm"
                        value={row.executor}
                        disabled={isUpdating}
                        onChange={(e) => setExecutor(row, e.target.value as JobExecutorPreference)}
                      >
                        <option value="auto">{row.job_kind === 'scheduled' ? 'Auto' : 'Auto (denylist)'}</option>
                        <option value="backend">Backend</option>
                        <option value="lambda">Lambda</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        row.effective_executor === 'lambda'
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {row.effective_executor}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right align-top">
                    <button
                      className="bg-blue-primary rounded px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      onClick={() => openRunModal(row)}
                      disabled={isRunning}
                    >
                      Run
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  No jobs match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editingSchedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Edit schedule</h3>
            <p className="mt-1 font-mono text-xs text-gray-500">{editingSchedule.handler_key}</p>
            <p className="mt-2 text-sm text-gray-600">
              Examples: <code>daily@06:00</code>, <code>weekly:tue@06:00</code>, <code>hourly@:05</code>,{' '}
              <code>every:20m</code>, <code>every:3d@04:00</code>. Comma-separate multiple ticks. After save, Next run
              updates immediately so you can verify the cadence.
            </p>

            <label className="mt-4 block text-xs font-semibold uppercase text-gray-500">schedule_expr</label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm"
              value={scheduleDraft}
              onChange={(e) => setScheduleDraft(e.target.value)}
            />

            <label className="mt-3 block text-xs font-semibold uppercase text-gray-500">timezone</label>
            <select
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm"
              value={timezoneDraft === 'US/Central' ? 'America/Chicago' : timezoneDraft}
              onChange={(e) => setTimezoneDraft(e.target.value)}
            >
              <option value="UTC">UTC</option>
              <option value="America/Chicago">America/Chicago</option>
            </select>

            <div className="mt-4 flex justify-end gap-2">
              <button
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                onClick={() => setEditingSchedule(null)}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="bg-blue-primary rounded px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                onClick={submitSchedule}
                disabled={isUpdating || !scheduleDraft.trim()}
              >
                {isUpdating ? 'Saving…' : 'Save schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {runTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {runTarget.job_kind === 'scheduled' ? 'Run scheduled job' : 'Run job'}
            </h3>
            <p className="mt-1 font-mono text-xs text-gray-500">{runTarget.handler_key}</p>

            {runTarget.job_kind === 'scheduled' ? (
              <p className="mt-2 text-sm text-gray-600">
                Triggers now using the selected runner: <strong>backend</strong> uses the daemon spawn path;{' '}
                <strong>lambda</strong> or <strong>auto</strong> (when Lambda is enabled) enqueues via fire-and-forget /
                SQS. Manual Run bypasses the overlap skip used by the scheduler.
              </p>
            ) : (
              <>
                <p className="mt-2 text-sm text-gray-600">
                  Provide JSON-serializable args/kwargs. Disabled handlers can still be force-run from here.
                </p>

                <label className="mt-4 block text-xs font-semibold uppercase text-gray-500">args (JSON array)</label>
                <textarea
                  className="mt-1 h-28 w-full rounded border border-gray-300 p-2 font-mono text-xs"
                  value={argsText}
                  onChange={(e) => setArgsText(e.target.value)}
                />

                <label className="mt-3 block text-xs font-semibold uppercase text-gray-500">kwargs (JSON object)</label>
                <textarea
                  className="mt-1 h-28 w-full rounded border border-gray-300 p-2 font-mono text-xs"
                  value={kwargsText}
                  onChange={(e) => setKwargsText(e.target.value)}
                />
              </>
            )}

            {runError && <p className="mt-2 text-sm text-red-600">{runError}</p>}

            <div className="mt-4 flex justify-end gap-2">
              <button
                className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                onClick={() => setRunTarget(null)}
                disabled={isRunning}
              >
                Cancel
              </button>
              <button
                className="bg-blue-primary rounded px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                onClick={submitRun}
                disabled={isRunning}
              >
                {isRunning ? 'Running…' : runTarget.job_kind === 'scheduled' ? 'Trigger now' : 'Queue job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobControlsTab;
